import {
  DocumentIcon,
  DocumentPlusIcon,
  PhotoIcon,
} from "@heroicons/react/24/solid";
import { FC } from "react";
import { isExcel, isPdf, removeFullUrl } from "helpers/file";
import { ClockIcon, TrashIcon } from "@heroicons/react/24/outline";

interface Props {
  url: string;
  name: string;
}
export const FilePreviewVerySmall: FC<Props> = ({ url, name }) => {
  return (
    <div className="flex p-1">
      <div className="flex items-center">
        {isPdf(name) ? (
          <DocumentIcon className="h-3 text-red-400" />
        ) : isExcel(name) ? (
          <DocumentPlusIcon className="h-3 text-green-600" />
        ) : (
          <PhotoIcon className="h-3 text-gray-400" />
        )}
        {isPdf(name) || isExcel(name) ? (
          <a
            href={url}
            download={name}
            className="ml-1 flex cursor-pointer flex-col space-y-1 text-gray-900 hover:text-gray-600"
          >
            <span
              className="truncate text-xs font-medium"
              style={{ maxWidth: "45ch" }}
            >
              {removeFullUrl(name)}
            </span>
          </a>
        ) : (
          <a
            href={`/image/${encodeURIComponent(url)}`}
            target="_blank"
            rel="noreferrer"
            className="ml-2 flex cursor-pointer flex-col space-y-1"
          >
            <span className="truncate text-sm font-semibold">
              {removeFullUrl(name)}
            </span>
            <span className="text-sm text-gray-500">Bild</span>
          </a>
        )}
      </div>
    </div>
  );
};

interface FilePreviewSmallProps {
  url: string;
  onDelete?: () => void;
  isInspectionPd?: boolean;
  name: string;
}
export const FilePreviewSmall: FC<FilePreviewSmallProps> = ({
  onDelete,
  url,
  name,
}) => {
  return (
    <div className="flex w-fit max-w-full rounded-lg border border-gray-100 p-3 shadow-sm">
      <div className="flex min-w-0 items-center">
        {isPdf(name) ? (
          <DocumentIcon className="h-6 shrink-0 text-red-400" />
        ) : isExcel(name) ? (
          <DocumentPlusIcon className="h-6 shrink-0 text-green-600" />
        ) : (
          <PhotoIcon className="h-6 shrink-0 text-gray-400" />
        )}
        {isPdf(name) || isExcel(name) ? (
          <a
            href={url}
            download={name}
            className="ml-2 flex min-w-0 cursor-pointer flex-col space-y-1"
          >
            <span className="truncate text-sm font-semibold">
              {removeFullUrl(name)}
            </span>
            {/* <span className="text-sm text-gray-500">
              {getFileTypeName(name)}
            </span> */}
          </a>
        ) : (
          <a
            href={`/image/${encodeURIComponent(url)}`}
            target="_blank"
            rel="noreferrer"
            className="ml-2 flex min-w-0 cursor-pointer flex-col space-y-1"
          >
            <span className="truncate text-sm font-semibold">
              {removeFullUrl(name)}
            </span>
            {/* <span className="text-sm text-gray-500">Bild</span> */}
          </a>
        )}
      </div>
      {!!onDelete && (
        <div className="ml-auto flex shrink-0 items-center pl-3">
          <TrashIcon
            onClick={onDelete}
            className="h-4 cursor-pointer text-sm text-gray-500 hover:scale-110 hover:transform hover:text-red-400"
          />
        </div>
      )}
    </div>
  );
};

// const getFileTypeName = (name: string) => {
//   if (isPdf(name)) {
//     return "PDF";
//   } else if (isExcel(name)) {
//     return "Excel";
//   } else {
//     return "";
//   }
// };

interface InspectionPDFPreviewProps {
  onClick: () => void;
  inspectionName: string;
  isLoading?: boolean;
}
export const InspectionPDFPreviewSmall: FC<InspectionPDFPreviewProps> = ({
  onClick,
  inspectionName,
  isLoading = false,
}) => {
  return (
    <div
      className="relative flex w-fit max-w-full cursor-pointer rounded-lg border border-gray-100 p-3 shadow-sm hover:shadow-md"
      onClick={onClick}
    >
      <div className="flex min-w-0 items-center">
        <DocumentIcon className="h-6 shrink-0 text-red-400" />
        <div className="ml-2 flex min-w-0 flex-col space-y-1">
          <span className="truncate text-sm font-semibold text-gray-900">
            {inspectionName}
          </span>
          {/* <span className="text-sm text-gray-500">PDF</span> */}
        </div>
        {isLoading && <Loader />}
      </div>
    </div>
  );
};

interface PendingPDFCardSmallProps {
  title: string;
  subtitle: string;
}
/** Kompakt platshållare för en PDF som genereras först när flödet är slutfört. */
export const PendingPDFCardSmall: FC<PendingPDFCardSmallProps> = ({
  title,
  subtitle,
}) => (
  <div className="flex w-fit max-w-full items-center gap-3 rounded-lg border border-dashed border-gray-300 p-3">
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100">
      <ClockIcon className="h-4 w-4 text-gray-500" />
    </div>
    <div className="min-w-0">
      <p className="truncate text-sm font-semibold text-gray-900">{title}</p>
      <p className="truncate text-xs text-gray-500">{subtitle}</p>
    </div>
  </div>
);

const Loader = () => (
  <svg
    className="absolute right-2 h-7 w-7 animate-spin text-cyan-500"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);
